/**
 * Legacy hotel orders: FX + handling fee from payments.snapshot (historical, not live).
 */

function finiteNum(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function finitePositive(value) {
  const n = finiteNum(value);
  return n != null && n > 0 ? n : null;
}

function tryParseJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    const o = JSON.parse(value);
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}

function parsePaymentSnapshot(payment) {
  const snap = tryParseJson(payment?.snapshot);
  return snap && typeof snap === "object" ? snap : null;
}

function paymentActivityKey(p) {
  const t = p?.paid_at || p?.updated_at || "";
  return String(t);
}

/**
 * Best paid payment row with snapshot (most recent).
 * @param {Array<object>|null|undefined} orderPayments
 */
export function pickBestPaidPaymentWithSnapshot(orderPayments) {
  if (!Array.isArray(orderPayments) || orderPayments.length === 0) return null;
  const paid = orderPayments.filter(
    (p) => String(p?.payment_status || "").toLowerCase() === "paid"
  );
  if (!paid.length) return null;
  const withSnap = paid.filter((p) => parsePaymentSnapshot(p));
  const pool = withSnap.length ? withSnap : paid;
  return [...pool].sort((a, b) => paymentActivityKey(b).localeCompare(paymentActivityKey(a)))[0];
}

/**
 * @param {Array<object>|null|undefined} orderPayments
 * @param {{ logTrace?: boolean, orderId?: * }} [opts]
 */
export function resolveHotelPaymentSnapshot(orderPayments, opts = {}) {
  const orderId = opts.orderId ?? null;
  const logTrace = opts.logTrace !== false;
  const payment = pickBestPaidPaymentWithSnapshot(orderPayments);
  const snap = payment ? parsePaymentSnapshot(payment) : null;

  const paymentBlock =
    snap?.payment && typeof snap.payment === "object" ? snap.payment : {};
  const breakdown =
    snap?.breakdown && typeof snap.breakdown === "object" ? snap.breakdown : {};

  const exchangeRate =
    finitePositive(paymentBlock.exchange_rate_used) ??
    finitePositive(payment?.exchange_rate_used);

  const paidUsd =
    finitePositive(paymentBlock.paid_usd) ?? finitePositive(payment?.paid_usd);
  const paidIls =
    finitePositive(paymentBlock.paid_ils) ??
    finitePositive(payment?.paid_ils) ??
    finitePositive(payment?.paid_amount_ils);

  const handlingFeeUsd =
    finitePositive(breakdown.handling_fee_usd) ??
    finitePositive(payment?.handling_fee_usd);

  const calculatedHandlingFeeIls =
    handlingFeeUsd != null && exchangeRate != null
      ? Math.round(handlingFeeUsd * exchangeRate * 100) / 100
      : null;

  const totalUsd =
    finitePositive(snap?.screen_price_usd) ??
    finitePositive(payment?.screen_price_usd) ??
    paidUsd;

  let totalIls = paidIls;
  if (totalIls == null && totalUsd != null && exchangeRate != null) {
    totalIls = Math.round(totalUsd * exchangeRate * 100) / 100;
  }

  const tracePayload = {
    order_id: orderId,
    exchange_rate: exchangeRate,
    source: exchangeRate != null ? "payments.snapshot" : null,
    paid_usd: paidUsd,
    paid_ils: paidIls,
    handling_fee_usd: handlingFeeUsd,
    calculated_handling_fee_ils: calculatedHandlingFeeIls,
    total_usd: totalUsd,
    total_ils: totalIls,
    payment_id: payment?.id ?? payment?.payment_id ?? null,
  };

  if (logTrace && exchangeRate != null) {
    console.log("[HOTEL_LEGACY_PAYMENT_SNAPSHOT_FX_TRACE]", tracePayload);
  }

  return {
    exchangeRate,
    exchangeRatePath: exchangeRate != null
      ? "payments.snapshot.payment.exchange_rate_used"
      : null,
    paidUsd,
    paidIls,
    handlingFeeUsd,
    calculatedHandlingFeeIls,
    totalUsd,
    totalIls,
    hasHandlingFeeUsd: handlingFeeUsd != null,
    trace: tracePayload,
    payment,
  };
}

/**
 * @param {Array<object>|null|undefined} orderPayments
 * @param {{ logTrace?: boolean, orderId?: * }} [opts]
 */
export function resolveHotelPaymentSnapshotRate(orderPayments, opts = {}) {
  const resolved = resolveHotelPaymentSnapshot(orderPayments, opts);
  return {
    exchangeRate: resolved.exchangeRate,
    sourcePath: resolved.exchangeRatePath,
    ...resolved,
  };
}
